/**
 * Os campos de UM documento de apoio, na forma do tipo escolhido (E98.F5.T07).
 *
 * Componente só de desenho: quem guarda os valores e decide quando gravar é o cartão do
 * documento. A forma de cada campo vem de `CAMPOS_POR_TIPO`, que tem paridade com os contratos
 * do servidor — um campo que ele não aceita não aparece aqui.
 */
import { ptBR } from '@/i18n/pt-BR';
import { chaveDoRotulo } from '@/lib/chave_do_rotulo';
import {
  CAMPOS_POR_TIPO,
  type CampoDaLeitura,
  type TipoLido,
  type ValoresDaLeitura,
} from '@/lib/documentos_de_apoio';

import { CampoTexto } from './ProtocoloFormCampos';

const t = ptBR.documentosDeApoio;

type RotuloDoCampo = keyof typeof t.campos;

function tipoDoInput(campo: CampoDaLeitura): 'text' | 'date' | 'time' {
  if (campo.formato === 'horario') return 'time';
  return campo.formato === 'data' ? 'date' : 'text';
}

function modoDoTeclado(campo: CampoDaLeitura): 'decimal' | 'numeric' | undefined {
  if (campo.formato === 'reais') return 'decimal';
  return campo.formato === 'ano' ? 'numeric' : undefined;
}

export interface CamposDaLeituraDeApoioProps {
  idPrefix: string;
  tipo: TipoLido;
  valores: ValoresDaLeitura;
  disabled: boolean;
  onChange: (chave: string, valor: string) => void;
}

export function CamposDaLeituraDeApoio({
  idPrefix,
  tipo,
  valores,
  disabled,
  onChange,
}: CamposDaLeituraDeApoioProps): JSX.Element {
  return (
    <div className="grid gap-sc-3 sm:grid-cols-2" data-testid={`${idPrefix}-campos`}>
      {CAMPOS_POR_TIPO[tipo].map((campo) => {
        const modo = modoDoTeclado(campo);
        return (
          <CampoTexto
            key={campo.chave}
            id={`${idPrefix}-${campo.chave}`}
            label={t.campos[chaveDoRotulo(campo.chave) as RotuloDoCampo]}
            value={valores[campo.chave] ?? ''}
            onChange={(valor) => onChange(campo.chave, valor)}
            disabled={disabled}
            type={tipoDoInput(campo)}
            mono={campo.formato !== 'texto' || campo.chave === 'chave_de_acesso'}
            {...(campo.formato === 'lista'
              ? { hint: t.identificadoresDica, colSpan: 'full' as const }
              : {})}
            {...(modo === undefined ? {} : { inputMode: modo })}
          />
        );
      })}
    </div>
  );
}
